// Parsing + template generation for bulk-importing Bible Plans from a
// spreadsheet. Layout (matches the admin's working spreadsheet): one plan per
// column — row 1 is the plan name, row 2 is the description, and every row
// from 3 on down is one "Book Chapter" reference (e.g. "Genesis 3",
// "1 Corinthians 13"). A sheet literally named "Instructions" is skipped so
// the template can carry a how-to page without it being read as a plan.
import * as XLSX from 'xlsx';
import { BIBLE_BOOKS } from './bibleData';

// Alternate spellings seen in plan spreadsheets that don't exactly match a
// BIBLE_BOOKS name (e.g. the common singular "Psalm" vs. this app's "Psalms").
const BOOK_NAME_ALIASES = {
  'psalm': 'psalms',
  'song of songs': 'song of solomon',
  'canticles': 'song of solomon',
};

function findBook(rawBookName) {
  const normalized = rawBookName.trim().toLowerCase();
  const aliased = BOOK_NAME_ALIASES[normalized] || normalized;
  return BIBLE_BOOKS.find((b) => b.name.toLowerCase() === aliased) || null;
}

// Parses one "Book Chapter" line, e.g. "Genesis 03" or "1 Corinthians 13".
// Leading zeros are fine — parseInt strips them. Returns either a parsed
// { raw, book, bookName, chapter } or an { raw, error } describing why not.
export function parseChapterLine(line) {
  const raw = String(line).trim();
  if (!raw) return null;
  const match = raw.match(/^(.+?)\s+(\d+)$/);
  if (!match) return { raw, error: `Couldn't read "${raw}" as "Book Chapter"` };
  const [, bookPart, chapterStr] = match;
  const book = findBook(bookPart);
  if (!book) return { raw, error: `Unknown book "${bookPart.trim()}"` };
  const chapter = parseInt(chapterStr, 10);
  if (chapter < 1 || chapter > book.chapters) {
    return { raw, error: `${book.name} only has ${book.chapters} chapters` };
  }
  return { raw, book: book.id, bookName: book.name, chapter };
}

// Parses an uploaded workbook's ArrayBuffer into plan drafts. Pure/no side
// effects — callers decide what (if anything) to do with the results.
export function parsePlansWorkbook(arrayBuffer) {
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const drafts = [];

  workbook.SheetNames.forEach((sheetName) => {
    if (sheetName.trim().toLowerCase() === 'instructions') return;

    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: '' });
    if (rows.length === 0) return;
    const colCount = rows.reduce((max, row) => Math.max(max, row.length), 0);

    for (let col = 0; col < colCount; col++) {
      const name = String(rows[0]?.[col] ?? '').trim();
      if (!name) continue;
      const description = String(rows[1]?.[col] ?? '').trim();

      const lines = [];
      for (let row = 2; row < rows.length; row++) {
        const cell = String(rows[row]?.[col] ?? '').trim();
        if (cell) lines.push(cell);
      }
      const parsedLines = lines.map(parseChapterLine).filter(Boolean);

      drafts.push({
        key: `${sheetName}:${col}`,
        sheetName,
        name,
        description,
        chapters: parsedLines.filter((p) => !p.error),
        errors: parsedLines.filter((p) => p.error),
      });
    }
  });

  return drafts;
}

// Builds a downloadable template workbook in the exact same layout, with one
// filled-in example plan so the format is obvious, plus a few blank columns
// ready to fill in, and a separate "Instructions" sheet (skipped on import).
export function buildTemplateWorkbook() {
  const exampleColumn = [
    'The Story of Ruth',
    'A whole short book, four chapters, about loyalty and love in hard times.',
    'Ruth 1',
    'Ruth 2',
    'Ruth 3',
    'Ruth 4',
  ];
  const blankColumns = 5;
  // 200 rows of headroom — a plan's chapter list (row 3 down) shouldn't ever
  // run past the template's pre-sized area.
  const rowCount = Math.max(exampleColumn.length, 200);

  const dataRows = [];
  for (let r = 0; r < rowCount; r++) {
    const row = [exampleColumn[r] ?? ''];
    for (let c = 0; c < blankColumns; c++) row.push('');
    dataRows.push(row);
  }
  const dataSheet = XLSX.utils.aoa_to_sheet(dataRows);
  dataSheet['!cols'] = [{ wch: 42 }, ...Array(blankColumns).fill({ wch: 30 })];

  const instructionsRows = [
    ['How to fill out this template'],
    [''],
    ['Each column is one plan.'],
    ['Row 1: the plan name.'],
    ['Row 2: the plan description.'],
    ['Row 3 and down: one chapter per row, as "Book Chapter" — e.g. "Genesis 3" or "1 Corinthians 13".'],
    ['Leave a column blank to skip it.'],
    ['This sheet is ignored on import — only the "Bible Plans" sheet is read.'],
  ];
  const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsRows);
  instructionsSheet['!cols'] = [{ wch: 90 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, dataSheet, 'Bible Plans');
  XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');
  return workbook;
}

export function downloadTemplateWorkbook(filename = 'Bible Plans Template.xlsx') {
  XLSX.writeFile(buildTemplateWorkbook(), filename);
}
